import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EObservableElementProp,
  IObservableElementProps,
  OBSERVABLE_ELEMENT_PROPS_SET,
} from './observable-element.types';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AnimationFrameService } from '../../services';
import { ComponentPrototype, isArray } from '../../shared';

type ObservableProps = Observable<IObservableElementProps>;

@Directive({
  selector: '[appObservableElement]',
})
export class ObservableElementDirective extends ComponentPrototype implements OnInit, OnDestroy {
  @Input('appObservableElement') public observingProps: any;

  @Input() public observable$: BehaviorSubject<IObservableElementProps>;

  @Output() public changed: EventEmitter<IObservableElementProps> = new EventEmitter<IObservableElementProps>();

  @Output() public componentReady: EventEmitter<ObservableProps> = new EventEmitter<ObservableProps>();

  private readonly core$: BehaviorSubject<IObservableElementProps> = new BehaviorSubject<IObservableElementProps>(null);

  private readonly isRunning$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private ref: ElementRef, private service: AnimationFrameService) {
    super();
  }

  ngOnInit(): void {
    const { isRunning$, core$, observable$, ref, changed, componentReady, service } = this;

    const elem = ref.nativeElement;

    const initializeObservingProps = (): void => {
      const observingProps: IObservableElementProps = {};
      const elemRect = elem.getBoundingClientRect();

      this.observingProps &&
        (isArray(this.observingProps) ? this.observingProps : `${this.observingProps}`.split(/,|;/)).forEach(s => {
          const prop = s.trim().toLowerCase();

          Object.values(EObservableElementProp).includes(prop) && (observingProps[prop] = elemRect[prop]);
        });

      !Object.keys(observingProps).length &&
        OBSERVABLE_ELEMENT_PROPS_SET.SIZE.forEach(prop => (observingProps[prop] = elemRect[prop]));

      core$.next(observingProps);
      observable$?.next(observingProps);
    };

    const checkChanges = (): IObservableElementProps => {
      const current = elem.getBoundingClientRect();
      const cached = core$.value;
      let props = null;

      Object.keys(cached).forEach(key => {
        if (cached[key] !== current[key]) {
          props = props || {};
          props[key] = current[key];
        }
      });

      return props ? { ...cached, ...props } : null;
    };

    this.registerSubscriptions(
      core$
        .pipe(
          filter(() => isRunning$.value),
          tap(value => changed.emit(value)),
          tap(value => observable$?.next(value))
        )
        .subscribe(),

      isRunning$
        .pipe(
          filter(isRunning => !!isRunning),
          switchMap(() => service.on$),
          tap(() => {
            const changes = isRunning$.value && checkChanges();
            changes && core$.next(changes);
          })
        )
        .subscribe()
    );

    setTimeout(() => {
      initializeObservingProps();
      isRunning$.next(true);
      componentReady.emit(core$.asObservable());
    });
  }

  ngOnDestroy(): void {
    this.isRunning$.next(false);
    super.ngOnDestroy();
  }
}
